import merge from 'lodash/merge';
import {useEffect, useRef} from 'react';
import pxToRem from '../../../../tailwindcss/functions/pxToRem';
import {t} from '../../../common/getTranslations';
import {twMerge} from '../../../common/twMerge';
import {
  CommonMobileHeaderType,
  MOBILE_HEADER_HEIGHT_PX,
} from '../../../header/constants';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {StaticImage} from '../../atoms/StaticImage/StaticImage';
import {StaticImageType} from '../../atoms/StaticImage/StaticImageType.enum';
import {Text} from '../../atoms/Text/Text';
import {IconWithDot} from '../../molecules/IconWithDot';
import {ProfileImage} from '../ProfileImage/ProfileImage';
import {MobileCommonHeaderProps} from './MobileCommonHeaderProps.interface';

export function MobileCommonHeader({
  title,
  headerType,
  onClickProfile,
  onClickBack,
  onClickLogo,
  onClickHome,
  onClickInven,
  onClickGift,
  onClickSearch,
  onClickCashCharge,
  shouldShowUserThumbnail,
  userThumbnailUrl,
  isGiftUpdated,
  isKakaotalk,
  className,
  style,
  ...props
}: MobileCommonHeaderProps) {
  const leftAreaRef = useRef<HTMLDivElement>(null);
  const rightAreaRef = useRef<HTMLDivElement>(null);
  const showTitle = headerType !== CommonMobileHeaderType.MainAndSub;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <headerType 변경 시 재계산 필요>
  useEffect(() => {
    if (showTitle && leftAreaRef.current && rightAreaRef.current) {
      leftAreaRef.current && (leftAreaRef.current.style.minWidth = '');
      rightAreaRef.current && (rightAreaRef.current.style.minWidth = '');

      const leftAreaWidth = leftAreaRef.current.offsetWidth;
      const rightAreaWidth = rightAreaRef.current.offsetWidth;

      leftAreaWidth > rightAreaWidth
        ? (rightAreaRef.current.style.minWidth = pxToRem(leftAreaWidth))
        : (leftAreaRef.current.style.minWidth = pxToRem(rightAreaWidth));
    }
  }, [headerType, showTitle]);

  return (
    <div
      className={twMerge(
        'relative flex w-full items-center bg-theme-bg-base px-18pxr',
        className
      )}
      style={merge(
        {
          height: MOBILE_HEADER_HEIGHT_PX,
        },
        style
      )}
      {...props}
    >
      {/* Header 좌측 영역입니다. */}
      <div
        ref={leftAreaRef}
        className={twMerge(
          'flex shrink-0 items-center',
          headerType === CommonMobileHeaderType.Cash && 'pr-20pxr'
        )}
      >
        {/* 카톡 웹뷰에서는 로고를 노출하지 않습니다. */}
        {!isKakaotalk && headerType === CommonMobileHeaderType.MainAndSub && (
          <button>
            <StaticImage
              className="h-24pxr w-110pxr cursor-pointer"
              imageType={StaticImageType.LogoBiTextL}
              onClick={onClickLogo}
              alt="카카오페이지 홈으로 이동"
            />
          </button>
        )}
        {headerType !== CommonMobileHeaderType.MainAndSub && (
          <button>
            <Icon
              type={IconType.NavigationBack}
              ariaHidden={false}
              onClick={onClickBack}
            />
          </button>
        )}
        {SHOW_BACK_ICON_HEADER_TYPE_LIST.includes(headerType) && (
          <button>
            <Icon
              className="ml-16pxr"
              type={IconType.NavigationHomeMw}
              ariaHidden={false}
              onClick={onClickHome}
            />
          </button>
        )}
        {/* 홈버튼이 노출되지 않고 카톡일 때는 X 버튼을 노출합니다. */}
        {isKakaotalk &&
          !SHOW_BACK_ICON_HEADER_TYPE_LIST.includes(headerType) && (
            <Icon
              type={IconType.NavigationClose}
              className="ml-16pxr"
              ariaHidden={false}
              onClick={() => {
                window.location.assign('kakaotalk://web/close ');
              }}
            />
          )}
      </div>
      {/* Header 중앙 영역입니다. */}
      {headerType !== CommonMobileHeaderType.MainAndSub && (
        <div className="flex flex-1 items-center justify-center px-10pxr">
          <Text className="font-medium1-bold line-clamp-1 text-theme-solid-100">
            {title}
          </Text>
        </div>
      )}
      {/* Header 우측 영역입니다. */}
      <div
        ref={rightAreaRef}
        className={twMerge(
          'flex shrink-0 items-center justify-end',
          !showTitle && 'flex-1'
        )}
        {...putTiaraObjectProps({
          click: {layer1: '숏컷'},
        })}
      >
        {SHOW_SEARCH_ICON_HEADER_TYPE_LIST.includes(headerType) && (
          <button>
            <Icon
              type={IconType.NavigationSearch}
              className={twMerge(
                headerType !== CommonMobileHeaderType.My && 'mr-16pxr',
                headerType === CommonMobileHeaderType.MainAndSub &&
                  'under-foldable-front:h-18pxr under-foldable-front:w-18pxr'
              )}
              ariaHidden={false}
              onClick={onClickSearch}
            />
          </button>
        )}
        {headerType === CommonMobileHeaderType.MainAndSub && (
          <>
            <button>
              <Icon
                type={IconType.NavigationCabinetWeb}
                className="mr-16pxr under-foldable-front:h-18pxr under-foldable-front:w-18pxr"
                ariaHidden={false}
                onClick={onClickInven}
              />
            </button>
            <button className="relative mr-16pxr">
              <IconWithDot
                iconType={IconType.NavigationGiftWeb}
                isShowDot={isGiftUpdated}
                onClick={onClickGift}
                ariaHidden={true}
                aria-label="선물함"
                className="under-foldable-front:h-18pxr under-foldable-front:w-18pxr"
              />
            </button>
          </>
        )}
        {SHOW_MY_ICON_HEADER_TYPE_LIST.includes(headerType) && (
          <>
            {shouldShowUserThumbnail && (
              <button
                className={twMerge(
                  'h-24pxr w-24pxr cursor-pointer overflow-hidden rounded-full inner-line-theme-transparent-20 inner-line-rounded-full inner-line-0.5px',
                  headerType === CommonMobileHeaderType.MainAndSub &&
                    'under-foldable-front:h-18pxr under-foldable-front:w-18pxr'
                )}
                onClick={onClickProfile}
              >
                {userThumbnailUrl && (
                  <ProfileImage thumbnailUrl={userThumbnailUrl} />
                )}
                {!userThumbnailUrl && (
                  <StaticImage imageType={StaticImageType.ProfileStatic} />
                )}
              </button>
            )}

            {!shouldShowUserThumbnail && (
              <button data-test="header-login-button">
                <Icon
                  type={IconType.NavigationMyWeb}
                  onClick={onClickProfile}
                  ariaHidden={false}
                  className={
                    headerType === CommonMobileHeaderType.MainAndSub
                      ? 'under-foldable-front:h-18pxr under-foldable-front:w-18pxr'
                      : ''
                  }
                />
              </button>
            )}
          </>
        )}
        {headerType === CommonMobileHeaderType.Cash && (
          <div className="flex shrink-0 items-center">
            <div
              className="flex items-center rounded-full border-1 border-theme-transparent-20 pl-6pxr pr-10pxr py-4pxr"
              onClick={onClickCashCharge}
            >
              <Icon
                type={IconType.ButtonCashFriendsStatic}
                ariaHidden={false}
              />
              <Text className="font-small2-bold ml-2pxr text-theme-solid-100">
                {t('add_cash_button')}
              </Text>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const SHOW_BACK_ICON_HEADER_TYPE_LIST = [
  CommonMobileHeaderType.TwoDepth,
  CommonMobileHeaderType.ThreeDepth,
  CommonMobileHeaderType.My,
  CommonMobileHeaderType.Cash,
];
const SHOW_SEARCH_ICON_HEADER_TYPE_LIST = [
  CommonMobileHeaderType.MainAndSub,
  CommonMobileHeaderType.TwoDepth,
  CommonMobileHeaderType.My,
];
const SHOW_MY_ICON_HEADER_TYPE_LIST = [
  CommonMobileHeaderType.MainAndSub,
  CommonMobileHeaderType.TwoDepth,
];
